import React, { useEffect } from 'react';
import { Alert, Chip, Grid, Link, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { AccountData, BookingType, OrderData, VoucherOut, VoucherType, WaitingListItemData } from '../../../api';
import { WalkInChip } from '../../../common/WalkInChip';
import TitledDrawer, { DRAWER_WIDTH } from '../../../common/ui/TitledDrawer';
// @ts-ignore
import { EditOrderProvider, useEditOrder } from './EditOrderContext';
import { EditOrderDialogIntent } from './components/footer/EditOrderDialogFooter';
import { EditOrderPanels } from './EditOrderTypes';
import { OrderDialogContent } from './OrderDialogContent';
import { RedeemPassDialogContent } from './RedeemPassDialogContent';
import { CreateOrderWizard } from './createOrderWizard/CreateOrderWizard';
import { IntentToggle, TOGGLE_GROUP_SX } from './components/IntentToggle';
import { OrderActionsBar } from '../../calendar-items/OrderActionsBar';
import {
  MANAGEMENT_COMMENT_FIELD_ID,
  orderCommentFieldId,
  RESOURCE_SELECTION_FIELD_ID,
} from '../../../common/OrderUtils';
import { getOrderPages } from '../../../utils/AccountUtils';

export { EditOrderPanels } from './EditOrderTypes';
export type { PersonalDetails, EditOrderFormValues } from './EditOrderTypes';
export { EditOrderDialogIntent } from './components/footer/EditOrderDialogFooter';

const WIZARD_INTENTS = [
  EditOrderDialogIntent.CreateOrder,
  EditOrderDialogIntent.CloneOrder,
  EditOrderDialogIntent.ConvertWaitListItem,
  EditOrderDialogIntent.RedeemVoucher,
];

const EditOrderDialogContent = ({
  editOrderDialogIntent,
  expandedPanel,
  setExpandedPanel,
  addOrder,
  updateOrder,
  closeEditOrderDialog,
  editOrderDialogState,
  voucherToRedeem,
  redeemVoucherCallback,
  voucherToEdit,
  waitingListItem,
  cloneSourceOrder,
  canToggleIntent,
  onToggleIntent,
  focusExtraItems,
  focusCommentFieldId,
}: {
  editOrderDialogIntent: EditOrderDialogIntent;
  expandedPanel?: EditOrderPanels;
  setExpandedPanel: (panelName?: EditOrderPanels) => void;
  addOrder?: (order: OrderData) => Promise<void>;
  updateOrder?: (order: OrderData) => void;
  closeEditOrderDialog: any;
  editOrderDialogState: any;
  voucherToRedeem?: VoucherOut;
  redeemVoucherCallback?: (voucher: VoucherOut) => void;
  voucherToEdit?: VoucherOut;
  waitingListItem?: WaitingListItemData;
  cloneSourceOrder?: OrderData;
  canToggleIntent?: boolean;
  onToggleIntent?: (intent: EditOrderDialogIntent) => void;
  focusExtraItems?: boolean;
  focusCommentFieldId?: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isRedeemPassIntent = editOrderDialogIntent === EditOrderDialogIntent.RedeemPass;
  const isCreateVoucherIntent = editOrderDialogIntent === EditOrderDialogIntent.CreateVoucher;
  // @ts-ignore
  const { editOrderState, applyRefreshedOrder, setVoucherType } = useEditOrder();
  const { closeEditOrderDrawer } = useCalendar();
  const isExistingOrder = editOrderDialogIntent === EditOrderDialogIntent.EditOrder && !!editOrderState.orderOrVoucher;

  return (
    <Grid
      container
      flexDirection="column"
      flexWrap="nowrap"
      width={isSmallScreen ? '100%' : DRAWER_WIDTH}
      sx={{ pt: { xs: '12px', md: '18px' }, paddingInline: { xs: '12px', md: '24px' }, pb: 0 }}
      overflow="hidden"
      height="100%">
      {isExistingOrder && (
        <Grid container alignItems="center" mb={1.5}>
          <OrderActionsBar
            order={editOrderState.orderOrVoucher as OrderData}
            onAfterAction={closeEditOrderDrawer}
            onOrderUpdated={(refreshedOrder) => {
              applyRefreshedOrder(refreshedOrder);
              updateOrder?.(refreshedOrder);
            }}
          />
        </Grid>
      )}
      {canToggleIntent && isRedeemPassIntent && (
        <IntentToggle editOrderDialogIntent={editOrderDialogIntent} onToggleIntent={onToggleIntent} />
      )}
      {isCreateVoucherIntent && (
        <Grid container mb={1.5}>
          <ToggleButtonGroup
            fullWidth
            value={editOrderState.voucherType}
            exclusive
            size="large"
            onChange={(_, val) => val && setVoucherType(val)}
            sx={TOGGLE_GROUP_SX}>
            <ToggleButton value={VoucherType.EXPERIENCE}>{t('vouchers.types.experience')}</ToggleButton>
            <ToggleButton value={VoucherType.MONETARY}>
              <Grid container gap={1} alignItems="center" justifyContent="center" flexWrap="nowrap">
                {t('vouchers.types.monetary')}
                <Chip size="small" color="primary" label={t('vouchers.newBadge')} />
              </Grid>
            </ToggleButton>
          </ToggleButtonGroup>
          {editOrderState.voucherType === VoucherType.MONETARY && (
            <Alert severity="info" sx={{ mt: 1.5, width: '100%' }}>
              {t('vouchers.monetaryIntro')}{' '}
              <Link component={RouterLink} to="/dashboard/account-settings#vouchers" onClick={closeEditOrderDialog}>
                {t('vouchers.monetarySettingsLink')}
              </Link>
            </Alert>
          )}
        </Grid>
      )}
      <Grid container flex={1} flexDirection="column" flexWrap="nowrap" overflow="hidden" minHeight={0}>
        {isRedeemPassIntent ? (
          <RedeemPassDialogContent
            expandedPanel={expandedPanel}
            setExpandedPanel={setExpandedPanel}
            addOrder={addOrder}
            closeEditOrderDialog={closeEditOrderDialog}
            editOrderDialogState={editOrderDialogState}
            initialPass={editOrderDialogState.passToRedeem}
          />
        ) : WIZARD_INTENTS.includes(editOrderDialogIntent) ? (
          <CreateOrderWizard
            editOrderDialogIntent={editOrderDialogIntent}
            editOrderDialogState={editOrderDialogState}
            closeEditOrderDialog={closeEditOrderDialog}
            addOrder={addOrder}
            voucherToRedeem={voucherToRedeem}
            redeemVoucherCallback={redeemVoucherCallback}
            waitingListItem={waitingListItem}
            cloneSourceOrder={cloneSourceOrder}
            canToggleIntent={canToggleIntent}
            onToggleIntent={onToggleIntent}
          />
        ) : (
          <OrderDialogContent
            editOrderDialogIntent={editOrderDialogIntent}
            focusExtraItems={focusExtraItems}
            focusCommentFieldId={focusCommentFieldId}
            expandedPanel={expandedPanel}
            setExpandedPanel={setExpandedPanel}
            updateOrder={updateOrder}
            closeEditOrderDialog={closeEditOrderDialog}
            editOrderDialogState={editOrderDialogState}
            voucherToRedeem={voucherToRedeem}
            voucherToEdit={voucherToEdit}
            waitingListItem={waitingListItem}
          />
        )}
      </Grid>
    </Grid>
  );
};

const EditOrderDialog = ({
  addOrder,
  updateOrder,
  editOrderDialogState,
  closeEditOrderDialog,
  redeemVoucherCallback,
}: {
  addOrder?: (order: OrderData) => Promise<void>;
  updateOrder?: (order: OrderData) => void;
  editOrderDialogState: any;
  closeEditOrderDialog: any;
  redeemVoucherCallback?: (voucher: VoucherOut) => void;
}) => {
  const { t } = useTranslation();
  const {
    order,
    orderPageId,
    voucherToRedeem,
    createVoucher,
    voucherToEdit,
    cloneOrder,
    waitingListItem,
    passToRedeem,
    focusExtraItems,
    openCatalog,
    openCheckout,
    focusComment,
    focusResources,
  } = editOrderDialogState;

  const { authState } = useAuth();
  const hasPassOrderPages = ((authState.account as unknown as AccountData).pass_order_pages?.length ?? 0) > 0;
  const focusFieldId = focusResources
    ? RESOURCE_SELECTION_FIELD_ID
    : focusComment && order
    ? orderCommentFieldId(
        order,
        getOrderPages(authState.account, true).find((orderPage) => orderPage.id === order.order_page_id),
      )
    : undefined;

  const [expandedPanel, setExpandedPanel] = React.useState<EditOrderPanels | undefined>(undefined);
  let baseIntent = EditOrderDialogIntent.CreateOrder;
  let title = t('calendar.create_order.title');
  if (createVoucher) {
    baseIntent = EditOrderDialogIntent.CreateVoucher;
    title = t('calendar.edit_event.create_voucher');
  }
  if (order) {
    baseIntent = EditOrderDialogIntent.EditOrder;
    title = `${t('calendar.edit_order.edit_order')} - #${order.order_number}`;
  }
  if (cloneOrder) {
    baseIntent = EditOrderDialogIntent.CloneOrder;
    title = `${t('calendar.edit_order.clone_order')}`;
  }
  if (voucherToRedeem) {
    baseIntent = EditOrderDialogIntent.RedeemVoucher;
    title = `${t('calendar.edit_order.redeemvoucher')} - #${voucherToRedeem.order_number}`;
  }
  if (voucherToEdit) {
    baseIntent = EditOrderDialogIntent.EditVoucher;
    title = `${t('vouchers.edit')} - #${voucherToEdit.order_number}`;
  }
  if (waitingListItem) {
    baseIntent = EditOrderDialogIntent.ConvertWaitListItem;
    title = `${t('calendar.edit_order.convertWaitListItem')} - ${waitingListItem.firstname} ${
      waitingListItem.lastname
    }`;
  }
  if (passToRedeem) {
    baseIntent = EditOrderDialogIntent.RedeemPass;
  }
  const [activeIntent, setActiveIntent] = React.useState<EditOrderDialogIntent>(baseIntent);
  const canToggleIntent = hasPassOrderPages && baseIntent === EditOrderDialogIntent.CreateOrder;

  // This dialog is always mounted, so useState(baseIntent) only runs once at app startup.
  // Without this effect, activeIntent stays stale across opens - e.g. after creating an order,
  // opening an existing order to edit would still submit to createOrder instead of editOrder.
  useEffect(() => {
    setActiveIntent(baseIntent);
    // Opened from "finalise bill"? Land on the panel holding the extra items. This has to live
    // in the same effect that clears the panel, which would otherwise undo it.
    // Likewise from an order's comment marker, onto the panel holding that comment.
    if (focusExtraItems || openCatalog || openCheckout) {
      setExpandedPanel(EditOrderPanels.ExperienceDetailPanel);
    } else if (focusFieldId) {
      if (focusFieldId === RESOURCE_SELECTION_FIELD_ID) {
        setExpandedPanel(EditOrderPanels.SeatingPanel);
      } else if (focusFieldId === MANAGEMENT_COMMENT_FIELD_ID) {
        setExpandedPanel(EditOrderPanels.ManagementPanel);
      } else {
        setExpandedPanel(EditOrderPanels.PersonalDetailsPanel);
      }
    } else {
      setExpandedPanel(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrderDialogState.isOpen, cloneOrder, voucherToEdit]);

  const handleToggleIntent = (intent: EditOrderDialogIntent) => {
    setExpandedPanel(undefined);
    setActiveIntent(intent);
  };

  const effectiveTitle = activeIntent === EditOrderDialogIntent.RedeemPass ? t('passes.redeem_pass') : title;

  return (
    <TitledDrawer
      title={effectiveTitle}
      titleChip={order?.booking_type === BookingType.WALK_IN ? <WalkInChip /> : undefined}
      open={editOrderDialogState.isOpen}
      onClose={(_: any, reason: any) => {
        if (reason !== 'backdropClick') {
          closeEditOrderDialog();
        }
      }}
      openAll={
        activeIntent !== EditOrderDialogIntent.RedeemPass && !WIZARD_INTENTS.includes(activeIntent)
          ? () => setExpandedPanel(EditOrderPanels.AllOpen)
          : undefined
      }>
      <EditOrderProvider
        key={activeIntent}
        order={cloneOrder ? undefined : order}
        cloneSourceOrder={cloneOrder ? order : undefined}
        orderPageId={orderPageId}
        voucherToRedeem={voucherToRedeem}
        voucherToEdit={voucherToEdit}
        waitingListItem={waitingListItem}
        isCreateOrderIntent={activeIntent === EditOrderDialogIntent.CreateOrder}>
        <EditOrderDialogContent
          editOrderDialogState={editOrderDialogState}
          closeEditOrderDialog={closeEditOrderDialog}
          addOrder={addOrder}
          updateOrder={updateOrder}
          editOrderDialogIntent={activeIntent}
          expandedPanel={expandedPanel}
          setExpandedPanel={setExpandedPanel}
          voucherToRedeem={voucherToRedeem}
          redeemVoucherCallback={redeemVoucherCallback}
          voucherToEdit={voucherToEdit}
          waitingListItem={waitingListItem}
          cloneSourceOrder={cloneOrder ? order : undefined}
          canToggleIntent={canToggleIntent}
          onToggleIntent={handleToggleIntent}
          focusExtraItems={focusExtraItems}
          focusCommentFieldId={focusFieldId}
        />
      </EditOrderProvider>
    </TitledDrawer>
  );
};

export default EditOrderDialog;
